// src/services/apaleo/companies.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import type { CompanyModel, RatePlanModel, BookingCancellationPolicy } from './types';
import type { ApaleoClient } from './client-contract';
import { IS_DEV, MockData, ALLOWED_PROPERTIES } from './config';

const part: ThisType<ApaleoClient> = {
async fetchCompanies(propertyId: string | null = null, textSearch: string | null = null): Promise<CompanyModel[]> {
  if (IS_DEV) {
    let all = MockData.MOCK_COMPANIES;
    if (propertyId && propertyId !== "All") {
      const mapping: Record<string, CompanyModel[]> = {
        BERLIN: [MockData.MOCK_COMPANIES[0], MockData.MOCK_COMPANIES[2]],
        GIESSEN: [MockData.MOCK_COMPANIES[1]],
        SIEGEN: [MockData.MOCK_COMPANIES[1]],
        WETZLAR: [MockData.MOCK_COMPANIES[2]]
      };
      all = mapping[propertyId] || MockData.MOCK_COMPANIES;
    }
    if (textSearch) {
      const s = textSearch.toLowerCase();
      all = all.filter(c =>
        c.name?.toLowerCase().includes(s) ||
        c.code?.toLowerCase().includes(s)
      );
    }
    return all;
  }
  try {
      const activePageSize = 100;
      let allCompanies: CompanyModel[] = [];
      let pageNumber = 1;
      let hasMore = true;
      let safetyCounter = 0;

      while (hasMore && safetyCounter < 50) {
        let url = `/rateplan/v1/companies?pageSize=${activePageSize}&pageNumber=${pageNumber}`;
        if (propertyId && propertyId !== "All") {
          url += `&propertyId=${propertyId}`;
        }
        if (textSearch) {
          url += `&textSearch=${encodeURIComponent(textSearch)}`;
        }
        const data = (await this._fetch(url)) as
          | { companies?: CompanyModel[] }
          | null;
        if (data && data.companies) {
          allCompanies = [...allCompanies, ...data.companies];
          hasMore = data.companies.length === activePageSize;
          pageNumber++;
        } else {
          hasMore = false;
        }
        safetyCounter++;
      }
      return allCompanies;
    } catch {
      return [];
    }
  },

  async fetchCompanyInvoicesReport(
    propertyId: string,
    from: string,
    to: string,
    options?: { onProgress?: (p: { page: number; rows: number }) => void },
  ): Promise<{ invoices: unknown[]; failedChunks: { start: string; end: string; error: string }[] }> {
    if (IS_DEV) {
      // Mock invoice list records (same normalized shape as production).
      return {
        invoices: [
          { company: { id: "COMP-1", name: "Siemens AG" }, invoice: { subTotal: { amount: 15430.50 }, invoiceDate: "2026-01-10", type: "Initial" } },
          { company: { id: "COMP-2", name: "Volkswagen" }, invoice: { subTotal: { amount: 8940.00 }, invoiceDate: "2026-01-12", type: "Advance" } },
          { company: { id: "COMP-3", name: "Google Germany" }, invoice: { subTotal: { amount: 12450.00 }, invoiceDate: "2026-01-14", type: "Initial" } }
        ],
        failedChunks: [],
      };
    }
    try {
      const start = new Date(from);
      const end = new Date(to);
      const chunks: { start: string; end: string }[] = [];
      let tempStart = new Date(start);

      while (tempStart < end) {
        let tempEnd = new Date(tempStart);
        tempEnd.setMonth(tempEnd.getMonth() + 1);
        if (tempEnd > end) {
          tempEnd = new Date(end);
        }
        chunks.push({
          start: this._localDateIso(tempStart),
          end: this._localDateIso(tempEnd)
        });
        tempStart = new Date(tempEnd);
      }

      let allInvoices: unknown[] = [];
      const failedChunks: { start: string; end: string; error: string }[] = [];
      const pageSize = 500; // spec maximum (docs/apaleo_finance.json:3552)
      const maxPages = 20; // safety cap: 20 x 500 = 10,000 invoices per month
      for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
        const chunk = chunks[chunkIdx];
        // dateFilter on the LIST endpoint is an ARRAY of expressions, each in
        // OPERATION_VALUE form, ALL of which must apply (spec line 3468) — so
        // it must be sent as TWO repeated params, NOT the comma-joined single
        // string the report endpoint used (that bug returned 200 with 0 rows).
        const dateFilterGte = `gte_${chunk.start}`;
        const dateFilterLt = `lt_${chunk.end}`;
        // 204 No Content = empty page — _fetch resolves true for 204;
        // true?.invoices is undefined -> empty page, not a failure.
        let pageNumber = 1;
        let hasMore = true;
        let safetyCounter = 0;
        while (hasMore && safetyCounter < maxPages) {
          const url = `/finance/v1/invoices?propertyIds=${encodeURIComponent(propertyId)}`
            + `&dateFilter=${encodeURIComponent(dateFilterGte)}`
            + `&dateFilter=${encodeURIComponent(dateFilterLt)}`
            + `&recipientType=Company&expand=company`
            + `&pageSize=${pageSize}&pageNumber=${pageNumber}`;
          const data = (await this._fetch(url).catch((err: unknown) => {
            console.warn("Failed to fetch company invoice list for chunk:", chunk, err);
            failedChunks.push({ start: chunk.start, end: chunk.end, error: err instanceof Error ? err.message : String(err) });
            return null;
          })) as { invoices?: unknown[]; count?: number } | null;
          const page = data?.invoices || [];
          if (page.length > 0) {
            allInvoices = allInvoices.concat(page);
            hasMore = page.length >= pageSize;
            pageNumber++;
          } else {
            hasMore = false;
          }
          safetyCounter++;
        }
        // Chunk-level diagnostic (console): rows fetched per month window.
        console.info(
          `[CompanyRevenue] ${chunk.start}..${chunk.end} -> fetched ${allInvoices.length} total invoices`,
        );
        // Progress callback (per month chunk) — global FetchProgressBar.
        options?.onProgress?.({ page: chunkIdx + 1, rows: allInvoices.length });
      }
      return { invoices: allInvoices, failedChunks };
    } catch (error) {
      console.error("Error fetching company invoices report:", error);
      return { invoices: [], failedChunks: [] };
    }
  },

  async fetchRatePlans(): Promise<RatePlanModel[]> {
    if (IS_DEV) return MockData.MOCK_RATE_PLANS;
    try {
      if (ALLOWED_PROPERTIES.length === 0) return [];
      const propertyFilter = ALLOWED_PROPERTIES.map(
        (id) => `propertyIds=${id}`,
      ).join("&");
      // Paginated loop (P.6 fix): the previous single fetch silently truncated
      // at Apaleo's default page size, hiding rate plans when tenants have more
      // than one page. Same while-loop shape as fetchReservations (line ~685).
      const pageSize = 100;
      const maxPages = 50; // safety cap: 50 x 100 = 5,000 rate plans
      const all: RatePlanModel[] = [];
      let pageNumber = 1;
      let hasMore = true;
      let safetyCounter = 0;
      while (hasMore && safetyCounter < maxPages) {
        const data = (await this._fetch(
          `/rateplan/v1/rate-plans?${propertyFilter}&pageSize=${pageSize}&pageNumber=${pageNumber}`,
        )) as { ratePlans?: RatePlanModel[] } | null;
        const page = data?.ratePlans || [];
        if (page.length > 0) {
          all.push(...page);
          hasMore = page.length === pageSize;
          pageNumber++;
        } else {
          hasMore = false;
        }
        safetyCounter++;
      }
      return all;
    } catch {
      return [];
    }
  },

  async fetchCancellationPolicy(policyId: string): Promise<BookingCancellationPolicy | null> {
    try {
      return (await this._fetch(
        `/rateplan/v1/cancellation-policies/${policyId}`,
      )) as BookingCancellationPolicy | null;
    } catch (error) {
      console.error(
        `Fehler beim Laden der Stornierungsbedingungen ${policyId}:`,
        error,
      );
      return null;
    }
  },

};

export default part;
